import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Stack,
  DialogActions,
  TextField,
} from '@mui/material';
import { getDeviceSnapshots, processSnapshot, getParsedSnapshot, getRawSnapshotText, deleteSnapshotSmart, bulkDeleteSnapshots, pullDeviceConfigNow } from '../../api/apiClient';
import { getEventSystemDevices } from '../../services/eventSystemApiService';
import JSONViewer from '../../components/common/JSONViewer';
import { useToast } from '../../hooks/useToast';

function formatCreatedAt(value) {
  if (!value) return '';
  try {
    // If ISO date
    if (typeof value === 'string' && value.includes('T')) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toLocaleString();
    }
    // If seconds epoch in string or number
    const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
    if (!isNaN(n)) {
      const d = new Date(n * 1000);
      if (!isNaN(d.getTime())) return d.toLocaleString();
    }
  } catch (_) {}
  return String(value);
}

const PAGE_SIZE_DEFAULT = 25;

const useTenantId = () => {
  try {
    const { useTenant } = require('../../libs/contexts/TenantContext');
    const { selectedTenant } = useTenant();
    if (selectedTenant?.id) return selectedTenant.id;
  } catch {}
  try {
    const { useAuth } = require('../../contexts/AuthContext');
    const { userData, userClaims } = useAuth();
    const tid = userClaims?.tenantId || userData?.tenantId || '';
    if (tid) return tid;
  } catch {}
  try {
    const href = typeof window !== 'undefined' ? window.location?.href : '';
    if (href) {
      const u = new URL(href);
      const tid = u.searchParams.get('tenantId') || u.searchParams.get('tenant');
      if (tid) return tid;
    }
  } catch {}
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const tid = window.localStorage.getItem('selectedTenantId') || window.localStorage.getItem('tenantId');
      if (tid) return tid;
    }
  } catch {}
  return '';
};

const ConfigSnapshotsPage = () => {
  const tenantId = useTenantId();
  const [deviceId, setDeviceId] = useState('');
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_DEFAULT);
  const [processingId, setProcessingId] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkKeepLatest, setBulkKeepLatest] = useState(10);
  const [bulkLimit, setBulkLimit] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const load = async () => {
      try {
        const list = await getEventSystemDevices(tenantId);
        const normalizeTypeKey = (d) => {
          let t = String(d?.deviceType || d?.type || '').toLowerCase();
          if (t === 'twilio_sms') t = 'sms';
          if (t === 'twilio_email') t = 'email';
          if (t === 'twilio_phone') t = 'phone';
          return t || 'other';
        };
        const filtered = (Array.isArray(list) ? list : []).filter(d => {
          const key = normalizeTypeKey(d);
          if (key === 'algo') return true;
          const model = String(d?.model || d?.properties?.model || '').toLowerCase();
          const label = String(d?.label || d?.name || '').toLowerCase();
          return model.includes('algo') || label.includes('algo');
        });
        setDevices(filtered);
        if (!deviceId && Array.isArray(filtered) && filtered.length > 0) {
          const firstId = filtered[0].id || filtered[0].deviceId || filtered[0].device_id;
          setDeviceId(firstId);
        }
      } catch (e) {
        console.error('Failed to load devices', e);
      }
    };
    if (tenantId) load();
  }, [tenantId]);

  const handleFetch = useCallback(async (opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!deviceId) {
      setRows([]);
      return [];
    }
    if (!silent) setLoading(true);
    try {
      const data = await getDeviceSnapshots(deviceId);
      const items = (data?.snapshots || []).map((s) => ({
        id: s.id || s.snapshot_id,
        device_id: s.device_id || deviceId,
        tenant_id: s.tenant_id || '',
        firmware: s.firmware || 'Unknown',
        size_bytes: s.size_bytes ?? s.size ?? 0,
        hash: s.hash || s.hash_sha256 || '',
        created_at: s.created_at || '',
        gcs_path: s.gcs_path || s.storage_uri || '',
        model: s.model || 'Unknown',
        source: s.source || 'agent',
        parsed: Boolean(s.parsed),
        mapped_fields: typeof s.mapped_fields === 'number' ? s.mapped_fields : 0,
        unknown_keys_count: Array.isArray(s.unknown_keys) ? s.unknown_keys.length : (typeof s.unknown_keys === 'number' ? s.unknown_keys : 0),
      }));
      setRows(items);
      setPage(0);
      return items;
    } catch (e) {
      console.error('Failed to load snapshots', e);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => { if (deviceId) { handleFetch(); } }, [deviceId, handleFetch]);

  const handleRefresh = useCallback(async () => {
    if (!deviceId) return;
    setRefreshing(true);
    try {
      toast.info('Pulling config from device...');
      await pullDeviceConfigNow(deviceId, { tenantId });
      // Wait longer for agent to upload and Firestore to sync
      await new Promise((r) => setTimeout(r, 5000));
      const attempts = 20;
      const intervalMs = 3000;
      const baselineIds = new Set((rows || []).map(r => r.id));
      const startTime = Date.now();
      
      for (let i = 0; i < attempts; i++) {
        const items = await handleFetch({ silent: true });
        // Check for new IDs OR very recent snapshots (within last 90 seconds)
        const hasNewId = items.some(it => !baselineIds.has(it.id));
        const hasRecentSnapshot = items.some(it => {
          try {
            const createdAt = it.created_at;
            if (!createdAt) return false;
            // Parse ISO string or epoch seconds
            let timestamp;
            if (typeof createdAt === 'string' && createdAt.includes('T')) {
              timestamp = new Date(createdAt).getTime();
            } else {
              const seconds = typeof createdAt === 'string' ? parseInt(createdAt, 10) : Number(createdAt);
              timestamp = seconds * 1000;
            }
            const ageMs = startTime - timestamp;
            return ageMs < 90000 && ageMs >= 0; // Within last 90 seconds
          } catch {
            return false;
          }
        });
        
        if (hasNewId || hasRecentSnapshot) {
          toast.success('New snapshot detected!');
          break;
        }
        if (i < attempts - 1) {
          await new Promise((r) => setTimeout(r, intervalMs));
        }
      }
    } catch (e) {
      console.error('Pull-now failed', e);
      toast.error('Device pull failed');
    } finally {
      setRefreshing(false);
      await handleFetch({ silent: true });
    }
  }, [deviceId, tenantId, handleFetch, rows, toast]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    const v = parseInt(e.target.value, 10);
    setRowsPerPage(v);
    setPage(0);
  };

  const onProcess = async (row) => {
    if (!row?.id) return;
    try {
      setProcessingId(row.id);
      await processSnapshot(row.id);
      toast.success('Parsed OK');
      await handleFetch();
    } catch (e) {
      console.error('Process failed', e);
      toast.error('Process failed');
    } finally {
      setProcessingId('');
    }
  };

  const onView = async (row) => {
    if (!row?.id) return;
    try {
      const obj = await getParsedSnapshot(row.id);
      setViewerData(obj || {});
      setViewerOpen(true);
    } catch (e) {
      console.error('Fetch parsed failed, trying raw fallback', e);
      try {
        const text = await getRawSnapshotText(row.id, row.device_id);
        setViewerData({ raw: text });
        setViewerOpen(true);
      } catch (e2) {
        console.error('Raw fallback failed', e2);
        toast.error('No parsed/raw content available');
      }
    }
  };

  const onDownloadRaw = async (row) => {
    if (!row?.id) return;
    try {
      const text = await getRawSnapshotText(row.id, row.device_id);
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `snapshot-${row.id}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download raw failed', e);
      toast.error('Download failed');
    }
  };

  const onDelete = (row) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSnapshotSmart(deleteTarget.id, deleteTarget.device_id);
      toast.success('Deleted');
      setDeleteOpen(false);
      setDeleteTarget(null);
      await handleFetch();
    } catch (e) {
      console.error('Delete failed', e);
      toast.error('Delete failed');
    }
  };

  const confirmBulkDelete = async () => {
    if (!deviceId) return;
    try {
      await bulkDeleteSnapshots(deviceId, { keepLatest: Number(bulkKeepLatest) || 0, limit: Number(bulkLimit) || 0 });
      toast.success('Bulk delete completed');
      setBulkOpen(false);
      await handleFetch();
    } catch (e) {
      console.error('Bulk delete failed', e);
      toast.error('Bulk delete failed');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Device Config Snapshots
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
        <FormControl sx={{ minWidth: 280 }} size="small">
          <InputLabel id="device-select-label">Device</InputLabel>
          <Select labelId="device-select-label" label="Device" value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
            {devices.map(d => {
              const id = d.id || d.deviceId || d.device_id;
              const name = d.name || d.displayName || id;
              return <MenuItem key={id} value={id}>{name}</MenuItem>;
            })}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleRefresh} disabled={!deviceId || loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </Box>

      <Paper elevation={0}>
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', minWidth: 1000 }}>
            <Table size="small" sx={{
              minWidth: 1200,
              '& tbody tr:nth-of-type(odd)': { backgroundColor: (theme) => theme.palette.action.hover },
            }}>
            <TableHead>
              <TableRow>
                <TableCell>Device ID</TableCell>
                <TableCell>Tenant ID</TableCell>
                <TableCell>Firmware</TableCell>
                <TableCell align="right">Size (bytes)</TableCell>
                <TableCell>Hash</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Parsed</TableCell>
                <TableCell align="right">Mapped</TableCell>
                <TableCell align="right">Unknown</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {deviceId ? 'No snapshots found' : 'Enter a Device ID and click Refresh'}
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.device_id}</TableCell>
                    <TableCell>{row.tenant_id}</TableCell>
                    <TableCell>{row.firmware}</TableCell>
                    <TableCell align="right">{row.size_bytes}</TableCell>
                    <TableCell title={row.hash}>{(row.hash || '').slice(0, 12)}</TableCell>
                    <TableCell>{formatCreatedAt(row.created_at)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.parsed ? 'Yes' : 'No'} color={row.parsed ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">{row.mapped_fields || 0}</TableCell>
                    <TableCell align="right">{(row.unknown_keys ?? row.unknown_keys_count ?? 0)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        {!row.parsed && (
                          <Button size="small" variant="outlined" onClick={() => onProcess(row)} disabled={processingId === row.id}>Process</Button>
                        )}
                        {row.parsed && (
                          <Button size="small" variant="contained" onClick={() => onView(row)}>View</Button>
                        )}
                        <Button size="small" onClick={() => onDownloadRaw(row)}>Download</Button>
                        <Button size="small" color="error" onClick={() => onDelete(row)}>Delete</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Parsed Snapshot</DialogTitle>
        <DialogContent dividers>
          <JSONViewer data={viewerData} />
        </DialogContent>
      </Dialog>

      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button variant="outlined" color="error" disabled={!deviceId} onClick={() => setBulkOpen(true)}>
          Bulk Delete…
        </Button>
      </Box>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Snapshot</DialogTitle>
        <DialogContent>
          Are you sure you want to delete snapshot {deleteTarget?.id}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)}>
        <DialogTitle>Bulk Delete Snapshots</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', gap: 2 }}>
          <TextField
            label="Keep Latest"
            type="number"
            size="small"
            value={bulkKeepLatest}
            onChange={(e) => setBulkKeepLatest(e.target.value)}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Limit (max to delete)"
            type="number"
            size="small"
            value={bulkLimit}
            onChange={(e) => setBulkLimit(e.target.value)}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmBulkDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfigSnapshotsPage;
